((root, factory) => {
  if (typeof module !== "undefined" && module.exports) {
    module.exports = factory();
  } else {
    factory();
  }
})(
  typeof globalThis !== "undefined"
    ? globalThis
    : typeof window !== "undefined"
      ? window
      : this,
  () => {
    // ── Pure helpers (no browser dependencies) ──

    function normalizeApiBaseUrl(value) {
      return String(value || "").replace(/\/+$/, "");
    }

    const commerceModes = new Set(["OPEN", "CATALOG_ONLY", "MAINTENANCE"]);
    const purchaseControlSelector = [
      "[data-ardexa-commerce-purchase]",
      'form[action*="/cart/add"] [name="add"]',
      "[data-product-form-submit]",
    ].join(", ");

    function normalizeCommerceStatus(value) {
      const mode = commerceModes.has(value?.mode) ? value.mode : "MAINTENANCE";
      const testVariantIds = Array.isArray(value?.testVariantIds)
        ? value.testVariantIds.map(String).filter((id) => /^\d+$/.test(id))
        : [];
      return {
        mode,
        purchasesEnabled: mode === "OPEN" && value?.purchasesEnabled === true,
        testPurchasesEnabled:
          mode === "CATALOG_ONLY" &&
          value?.testPurchasesEnabled === true &&
          testVariantIds.length > 0,
        testVariantIds,
        leadCaptureEnabled:
          mode !== "MAINTENANCE" && value?.leadCaptureEnabled === true,
        reason:
          typeof value?.reason === "string" && value.reason.trim()
            ? value.reason.trim()
            : null,
        version: Number.isSafeInteger(value?.version) ? value.version : 0,
        updatedAt:
          typeof value?.updatedAt === "string" ? value.updatedAt : null,
      };
    }

    function isAllowedTestCart(status, cart) {
      if (!status?.testPurchasesEnabled || !Array.isArray(cart?.items))
        return false;
      if (cart.items.length === 0) return false;
      const allowed = new Set(status.testVariantIds || []);
      return cart.items.every((item) =>
        allowed.has(String(item.variant_id ?? item.id ?? "")),
      );
    }

    function isAllowedTestProductForm(status, form) {
      if (!status?.testPurchasesEnabled || !form?.querySelector) return false;
      const variant = form.querySelector('[name="id"]');
      return (status.testVariantIds || []).includes(
        String(variant?.value ?? ""),
      );
    }

    async function fetchCommerceStatus(fetchFn, apiBaseUrl) {
      const apiBase = normalizeApiBaseUrl(apiBaseUrl);
      if (!apiBase || typeof fetchFn !== "function") {
        return normalizeCommerceStatus(null);
      }
      try {
        const response = await fetchFn(`${apiBase}/public/commerce-control`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) return normalizeCommerceStatus(null);
        return normalizeCommerceStatus(await response.json());
      } catch (_error) {
        return normalizeCommerceStatus(null);
      }
    }

    function normalizeCompanyProfile(value) {
      return {
        displayName:
          typeof value?.displayName === "string" && value.displayName.trim()
            ? value.displayName.trim()
            : "Ardexa",
        legalName:
          typeof value?.legalName === "string" ? value.legalName.trim() : "",
        vatNumber:
          typeof value?.vatNumber === "string" ? value.vatNumber.trim() : "",
        fiscalCode:
          typeof value?.fiscalCode === "string" ? value.fiscalCode.trim() : "",
        addressLine1:
          typeof value?.addressLine1 === "string"
            ? value.addressLine1.trim()
            : "",
        postalCode:
          typeof value?.postalCode === "string" ? value.postalCode.trim() : "",
        city: typeof value?.city === "string" ? value.city.trim() : "",
        province:
          typeof value?.province === "string" ? value.province.trim() : "",
        country: typeof value?.country === "string" ? value.country.trim() : "",
        email: typeof value?.email === "string" ? value.email.trim() : "",
        phone: typeof value?.phone === "string" ? value.phone.trim() : "",
        version: Number.isSafeInteger(value?.version) ? value.version : 0,
      };
    }

    async function fetchCompanyProfile(fetchFn, apiBaseUrl) {
      const apiBase = normalizeApiBaseUrl(apiBaseUrl);
      if (!apiBase || typeof fetchFn !== "function") {
        return normalizeCompanyProfile(null);
      }
      try {
        const response = await fetchFn(`${apiBase}/public/company-profile`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) return normalizeCompanyProfile(null);
        return normalizeCompanyProfile(await response.json());
      } catch (_error) {
        return normalizeCompanyProfile(null);
      }
    }

    function applyCompanyProfile(documentTarget, rawProfile) {
      const profile = normalizeCompanyProfile(rawProfile);
      documentTarget.documentElement.dataset.ardexaCompanyProfileVersion =
        String(profile.version);
      documentTarget
        .querySelectorAll("[data-ardexa-company-name]")
        .forEach((element) => {
          element.textContent = profile.displayName;
        });
      documentTarget
        .querySelectorAll("[data-ardexa-company-home-link]")
        .forEach((element) => {
          element.setAttribute("aria-label", `${profile.displayName} home`);
        });
      if (profile.legalName) {
        const details = [
          profile.legalName,
          [profile.addressLine1, profile.postalCode, profile.city, profile.province, profile.country].filter(Boolean).join(" "),
          profile.vatNumber ? `P. IVA: ${profile.vatNumber}` : "",
          profile.fiscalCode ? `C.F. ${profile.fiscalCode}` : "",
        ].filter(Boolean).join(" · ");
        documentTarget.querySelectorAll("[data-ardexa-company-legal]").forEach((element) => {
          element.textContent = details;
        });
      }
      return profile;
    }

    function createCommerceNotice(documentTarget, status) {
      const notice = documentTarget.createElement("div");
      notice.className = "ardexa-commerce-notice";
      notice.dataset.ardexaCommerceNotice = "true";
      notice.setAttribute("role", "status");

      const message = documentTarget.createElement("p");
      message.textContent =
        status.reason ||
        (status.mode === "CATALOG_ONLY"
          ? "Gli acquisti sono temporaneamente sospesi mentre completiamo le verifiche."
          : "Il servizio acquisti è temporaneamente in manutenzione.");
      notice.append(message);

      if (status.leadCaptureEnabled) {
        const link = documentTarget.createElement("a");
        link.href = "/pages/contact";
        link.textContent = "Richiedi informazioni";
        link.className = "button button--secondary";
        notice.append(link);
      }
      return notice;
    }

    function applyCommerceStatus(documentTarget, rawStatus) {
      const status = normalizeCommerceStatus(rawStatus);
      const enabled = status.purchasesEnabled && status.mode === "OPEN";
      documentTarget.documentElement.dataset.ardexaCommerceMode = status.mode;

      documentTarget
        .querySelectorAll("[data-ardexa-commerce-notice]")
        .forEach((notice) => notice.remove?.());

      const buttons = Array.from(
        documentTarget.querySelectorAll(purchaseControlSelector),
      );
      buttons.forEach((button) => {
        const productForm = button.closest?.('form[action*="/cart/add"]');
        const testEnabled = productForm
          ? isAllowedTestProductForm(status, productForm)
          : status.testPurchasesEnabled;
        const buttonEnabled = enabled || testEnabled;
        const cartEmpty = button.dataset.ardexaCartEmpty === "true";
        const managed = button.dataset.ardexaCommerceManaged === "true";
        if (testEnabled) {
          button.dataset.ardexaTestPurchaseEnabled = "true";
        } else {
          delete button.dataset.ardexaTestPurchaseEnabled;
        }
        if (!buttonEnabled) {
          if (!button.disabled) button.dataset.ardexaCommerceDisabled = "true";
          button.disabled = true;
        } else if (
          managed ||
          button.dataset.ardexaCommerceDisabled === "true"
        ) {
          button.disabled = cartEmpty;
          delete button.dataset.ardexaCommerceDisabled;
        }
        if (button.disabled) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
      });
      const testSurfaceEnabled =
        status.testPurchasesEnabled &&
        buttons.some((button) => {
          const form = button.closest?.('form[action*="/cart/add"]');
          return !form || isAllowedTestProductForm(status, form);
        });
      const surfaceEnabled = enabled || testSurfaceEnabled;

      documentTarget
        .querySelectorAll("[data-ardexa-commerce-purchase-container]")
        .forEach((container) => {
          container.hidden = !surfaceEnabled;
        });

      const protectedSurface = documentTarget.querySelector(
        "[data-ardexa-commerce-protected]",
      );
      if (protectedSurface)
        protectedSurface.hidden = !(enabled || status.testPurchasesEnabled);

      if (!surfaceEnabled && !status.testPurchasesEnabled) {
        const notice = createCommerceNotice(documentTarget, status);
        if (protectedSurface?.before) {
          protectedSurface.before(notice);
        } else {
          buttons[0]?.insertAdjacentElement?.("afterend", notice);
        }
      }

      return status;
    }

    function buildCheckoutStorageKey(sessionId) {
      return "ardexa_checkout_token:" + sessionId;
    }

    function buildIdempotencyStorageKey(cartToken) {
      return "ardexa_checkout_idempotency:" + (cartToken || "anonymous");
    }

    function buildCheckoutPath(pagePath, sessionId, baseOrigin) {
      if (baseOrigin === undefined && typeof window !== "undefined") {
        baseOrigin = window.location.origin;
      } else if (baseOrigin === undefined) {
        baseOrigin = "https://www.ardexa.it";
      }
      var url;
      try {
        url = new URL(pagePath || "/pages/checkout-ardexa", baseOrigin);
      } catch (e) {
        url = new URL("/pages/checkout-ardexa", baseOrigin);
      }
      if (url.origin !== baseOrigin) {
        url = new URL("/pages/checkout-ardexa", baseOrigin);
      }
      if (
        url.pathname === "/pages/checkout-ardexa" &&
        !url.searchParams.has("view")
      ) {
        url.searchParams.set("view", "checkout-ardexa-v3");
      }
      url.searchParams.set("checkout_id", sessionId);
      return url.pathname + url.search;
    }

    function getSessionIdFromUrl(href, baseOrigin) {
      if (baseOrigin === undefined && typeof window !== "undefined") {
        baseOrigin = window.location.origin;
      } else if (baseOrigin === undefined) {
        baseOrigin = "https://www.ardexa.it";
      }
      if (href === undefined && typeof window !== "undefined") {
        href = window.location.href;
      } else if (href === undefined) {
        href = "";
      }
      try {
        const searchParams = new URL(href, baseOrigin).searchParams;
        return searchParams.get("checkout_id") || searchParams.get("sid");
      } catch (e) {
        return null;
      }
    }

    function isPlausiblePhoneNumber(value) {
      const raw = String(value || "").trim();
      if (!raw || !/^[+\d\s().\/-]+$/.test(raw)) return false;
      const plusSigns = (raw.match(/\+/g) || []).length;
      if (plusSigns > 1 || (plusSigns === 1 && !raw.startsWith("+")))
        return false;
      const digitCount = raw.replace(/\D/g, "").length;
      return digitCount >= 7 && digitCount <= 15;
    }

    function normalizeDiscountCode(value) {
      return String(value || "").trim();
    }

    function getAppliedDiscountCodes(cart) {
      const titles = [];
      const addTitle = (value) => {
        const title = normalizeDiscountCode(value);
        if (
          title &&
          !titles.some(
            (existing) => existing.toLowerCase() === title.toLowerCase(),
          )
        ) {
          titles.push(title);
        }
      };

      (cart?.cart_level_discount_applications || []).forEach((application) => {
        if (application?.type !== "automatic") addTitle(application?.title);
      });
      (cart?.discount_codes || []).forEach((discount) => {
        if (discount?.applicable !== false) {
          addTitle(discount?.code || discount?.title);
        }
      });

      return titles;
    }

    function isDiscountApplied(cart, code) {
      const normalizedCode = normalizeDiscountCode(code).toLowerCase();
      if (!normalizedCode) return getAppliedDiscountCodes(cart).length === 0;
      return getAppliedDiscountCodes(cart).some(
        (appliedCode) => appliedCode.toLowerCase() === normalizedCode,
      );
    }

    async function updateShopifyCartDiscount(
      fetchFn,
      shopRoot,
      code,
      options = {},
    ) {
      const maxRetries = Number(options.maxRetries ?? 2);
      const waitFn =
        options.waitFn ||
        ((delay) => new Promise((resolve) => setTimeout(resolve, delay)));
      const randomFn = options.randomFn || Math.random;
      const rootPath = String(shopRoot || "/").replace(/\/?$/, "/");
      let attempt = 0;

      while (true) {
        const response = await fetchFn(`${rootPath}cart/update.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ discount: normalizeDiscountCode(code) }),
        });

        if (response.status === 429 && attempt < maxRetries) {
          attempt += 1;
          const delay = 250 * 2 ** (attempt - 1) + Math.round(randomFn() * 100);
          await waitFn(delay);
          continue;
        }

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(
            body.description || body.message || "discount_update_failed",
          );
          error.status = response.status;
          throw error;
        }
        return body;
      }
    }

    function bindCheckoutButtons(
      documentTarget,
      checkoutConfig,
      startCheckoutFn,
      windowTarget,
    ) {
      if (!checkoutConfig.enabled) return;
      if (documentTarget.documentElement.dataset.ardexaCheckoutBound === "true")
        return;

      documentTarget.documentElement.dataset.ardexaCheckoutBound = "true";
      documentTarget.addEventListener("click", async (event) => {
        const button = event.target?.closest?.(
          "[data-ardexa-checkout-trigger]",
        );
        if (!button) return;

        event.preventDefault();
        if (button.disabled) return;
        button.setAttribute("aria-busy", "true");
        button.classList.add("is-loading");
        try {
          await startCheckoutFn(button.dataset.ardexaCheckoutSource || "cart");
        } catch (error) {
          button.removeAttribute("aria-busy");
          button.classList.remove("is-loading");
          windowTarget.dispatchEvent(
            new windowTarget.CustomEvent("ardexa:checkout:error", {
              detail: {
                error: String(error?.message || error),
              },
            }),
          );
          windowTarget.alert?.(
            "Non siamo riusciti ad aprire il checkout Ardexa. Riprova tra poco.",
          );
        }
      });
    }

    // ── Node.js / CommonJS export ──

    if (typeof module !== "undefined" && module.exports) {
      return {
        normalizeApiBaseUrl: normalizeApiBaseUrl,
        buildCheckoutStorageKey: buildCheckoutStorageKey,
        buildIdempotencyStorageKey: buildIdempotencyStorageKey,
        buildCheckoutPath: buildCheckoutPath,
        getSessionIdFromUrl: getSessionIdFromUrl,
        isPlausiblePhoneNumber: isPlausiblePhoneNumber,
        normalizeDiscountCode: normalizeDiscountCode,
        getAppliedDiscountCodes: getAppliedDiscountCodes,
        isDiscountApplied: isDiscountApplied,
        updateShopifyCartDiscount: updateShopifyCartDiscount,
        bindCheckoutButtons: bindCheckoutButtons,
        normalizeCommerceStatus: normalizeCommerceStatus,
        isAllowedTestCart: isAllowedTestCart,
        fetchCommerceStatus: fetchCommerceStatus,
        applyCommerceStatus: applyCommerceStatus,
        normalizeCompanyProfile: normalizeCompanyProfile,
        fetchCompanyProfile: fetchCompanyProfile,
        applyCompanyProfile: applyCompanyProfile,
      };
    }

    // ── Browser-only code (preserves original behavior) ──

    var config = window.ardexaCheckout || {};
    let commerceStatus = normalizeCommerceStatus(null);
    let companyProfile = normalizeCompanyProfile(null);

    const refreshCommerceStatus = async () => {
      commerceStatus = await fetchCommerceStatus(
        window.fetch.bind(window),
        config.apiBaseUrl,
      );
      applyCommerceStatus(document, commerceStatus);
      window.dispatchEvent(
        new window.CustomEvent("ardexa:commerce:status", {
          detail: commerceStatus,
        }),
      );
      return commerceStatus;
    };

    const refreshCompanyProfile = async () => {
      companyProfile = await fetchCompanyProfile(
        window.fetch.bind(window),
        config.apiBaseUrl,
      );
      applyCompanyProfile(document, companyProfile);
      return companyProfile;
    };

    const getIdempotencyKey = (cart) => {
      const storageKey = buildIdempotencyStorageKey(cart?.token);
      const existing = window.sessionStorage.getItem(storageKey);
      if (existing) return existing;
      const key = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `ardexa-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.sessionStorage.setItem(storageKey, key);
      return key;
    };

    const getCart = async () => {
      const response = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("cart_fetch_failed");
      return response.json();
    };

    const createSession = async (cart, sourcePage = "cart") => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const response = await fetch(`${apiBase}/public/checkout/sessions`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Ardexa-Idempotency-Key": getIdempotencyKey(cart),
        },
        body: JSON.stringify({
          cart,
          source: {
            page: sourcePage,
            locale: config.locale || "it",
            checkoutPagePath: config.pagePath || "/pages/checkout-ardexa",
          },
        }),
      });
      if (!response.ok) throw new Error("checkout_session_create_failed");
      return response.json();
    };

    const getSession = async (sessionId) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}`,
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) throw new Error("checkout_session_fetch_failed");
      return response.json();
    };

    const refreshCart = async (sessionId, cart) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/cart`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ cart }),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) throw new Error("checkout_cart_refresh_failed");
      return response.json();
    };

    const updateCartDiscount = (code) =>
      updateShopifyCartDiscount(
        window.fetch.bind(window),
        window.Shopify?.routes?.root || "/",
        code,
      );

    const evaluateCheckoutDiscount = async (sessionId, payload) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/discount-evaluation`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload || {}),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "discount_not_applicable");
      }
      return response.json();
    };

    const getShippingCountries = async () => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/shipping-countries`,
        {
          credentials: "include",
          headers: { Accept: "application/json" },
        },
      );
      if (!response.ok) throw new Error("shipping_countries_fetch_failed");
      return response.json();
    };

    const getShippingRate = async (sessionId, address) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/shipping-rates`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(address),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "shipping_rate_fetch_failed");
      }
      return response.json();
    };

    const startNativeCheckout = async (sessionId, payload) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/native-checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "native_checkout_create_failed");
      }
      return response.json();
    };

    const createOrder = async (sessionId, payload) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/order`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "order_create_failed");
      }
      return response.json();
    };

    const startStripeCheckout = async (sessionId, payload) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      if (!apiBase) throw new Error("checkout_api_missing");
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!token) throw new Error("checkout_token_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/stripe-checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (response.status === 410) throw new Error("checkout_session_expired");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "stripe_checkout_create_failed");
      }
      return response.json();
    };

    const purchaseTrackingRequest = async (sessionId, acknowledge = false) => {
      const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
      const token = window.sessionStorage.getItem(
        buildCheckoutStorageKey(sessionId),
      );
      if (!apiBase || !token) throw new Error("purchase_tracking_auth_missing");
      const response = await fetch(
        `${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}/tracking/purchase${acknowledge ? "/ack" : ""}`,
        {
          method: acknowledge ? "POST" : "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("purchase_tracking_failed");
      return response.json();
    };

    const startCheckout = async (sourcePage = "cart") => {
      const currentCommerceStatus = await refreshCommerceStatus();
      const cart = await getCart();
      if (
        !currentCommerceStatus.purchasesEnabled &&
        !isAllowedTestCart(currentCommerceStatus, cart)
      ) {
        throw new Error("commerce_unavailable");
      }
      if (
        !cart ||
        !cart.item_count ||
        !Array.isArray(cart.items) ||
        cart.items.length === 0
      ) {
        throw new Error("cart_empty");
      }
      const session = await createSession(cart, sourcePage);
      window.sessionStorage.setItem(
        buildCheckoutStorageKey(session.id),
        session.token,
      );
      const checkoutPath = buildCheckoutPath(config.pagePath, session.id);
      if (!checkoutPath.startsWith("/"))
        throw new Error("checkout_path_invalid");
      window.location.assign(checkoutPath);
      return session;
    };

    const bindCheckoutButtonsForPage = () =>
      bindCheckoutButtons(document, config, startCheckout, window);

    window.ArdexaCheckoutBridge = {
      normalizeApiBaseUrl,
      buildCheckoutStorageKey,
      buildCheckoutPath,
      getSessionIdFromUrl,
      isPlausiblePhoneNumber,
      getCart,
      createSession,
      getSession,
      refreshCart,
      updateCartDiscount,
      evaluateCheckoutDiscount,
      getAppliedDiscountCodes,
      isDiscountApplied,
      getShippingCountries,
      getShippingRate,
      startNativeCheckout,
      createOrder,
      startStripeCheckout,
      getPurchaseTracking: (sessionId) =>
        purchaseTrackingRequest(sessionId, false),
      acknowledgePurchaseTracking: (sessionId) =>
        purchaseTrackingRequest(sessionId, true),
      startCheckout,
      getCommerceStatus: () => commerceStatus,
      refreshCommerceStatus,
      getCompanyProfile: () => companyProfile,
      refreshCompanyProfile,
      bindCheckoutButtons: bindCheckoutButtonsForPage,
    };

    const blockClosedCommerce = (event) => {
      if (commerceStatus.purchasesEnabled) return;
      const purchaseControl = event.target?.closest?.(purchaseControlSelector);
      const productForm = event.target?.closest?.('form[action*="/cart/add"]');
      if (!purchaseControl && !(event.type === "submit" && productForm)) return;
      if (
        commerceStatus.testPurchasesEnabled &&
        (!productForm || isAllowedTestProductForm(commerceStatus, productForm))
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation?.();
    };

    const reapplyCommerceStatus = () =>
      applyCommerceStatus(document, commerceStatus);

    const initialize = () => {
      bindCheckoutButtonsForPage();
      document.addEventListener("click", blockClosedCommerce, true);
      document.addEventListener("submit", blockClosedCommerce, true);
      document.addEventListener("shopify:section:load", reapplyCommerceStatus);
      document.addEventListener("cart:change", () =>
        window.setTimeout(reapplyCommerceStatus, 0),
      );
      void Promise.all([refreshCommerceStatus(), refreshCompanyProfile()]);
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initialize);
    } else {
      initialize();
    }
  },
);
